import React, { useState, useEffect } from 'react'
import { Card, Tag, Button, Row, Col, Spin, Alert, Switch, Divider, Table } from 'antd'
import moment from 'moment'
import Screen from 'Evolve/Components/Screen'
import { getSubscription, getInvoices, changeSubscription } from 'HttpRequests/billing'
import { getCurrentTeam } from 'Common'

const planOrder = ['startup', 'grow', 'scale', 'enterprise']

const planDetails = {
    startup:    { label: 'Startup',    monthly: '£20/mo',   annual: '£200/yr' },
    grow:       { label: 'Grow',       monthly: '£99/mo',   annual: '£990/yr' },
    scale:      { label: 'Scale',      monthly: '£199/mo',   annual: '£1990/yr' },
    enterprise: { label: 'Enterprise', monthly: '£400/mo',  annual: '£4,000/yr' },
}

const discountLabels = {
    '30DAYSFREE':    '30 Days Free',
    'JOHNSONS1MNTH': '1 Month Free',
    'LEADPM10':      '10% Off',
    'LEADPM20':      '20% Off',
}

const statusColor = {
    active:          'green',
    trialing:        'blue',
    past_due:        'orange',
    canceled:        'red',
    incomplete:      'gold',
    incomplete_expired: 'red',
}

const invoiceColumns = [
    {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: ts => moment.unix(ts).format('D MMM YYYY'),
    },
    {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        render: (amount, row) => `${row.currency === 'GBP' ? '£' : row.currency + ' '}${(amount / 100).toFixed(2)}`,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: status => (
            <Tag color={status === 'paid' ? 'green' : status === 'open' ? 'orange' : 'red'}>
                {status && status.toUpperCase()}
            </Tag>
        ),
    },
    {
        title: '',
        dataIndex: 'pdf',
        key: 'pdf',
        render: url => url ? <a href={url} target="_blank" rel="noreferrer">Download PDF</a> : null,
    },
]

const Billing = () => {
    const team = getCurrentTeam()

    const [subscription, setSubscription]     = useState(null)
    const [invoices, setInvoices]             = useState([])
    const [loading, setLoading]               = useState(true)
    const [changing, setChanging]             = useState(false)
    const [changeError, setChangeError]       = useState(null)
    const [changeSuccess, setChangeSuccess]   = useState(false)
    const [annual, setAnnual]                 = useState(false)
    const [selectedPlan, setSelectedPlan]     = useState(null)

    useEffect(() => {
        Promise.all([
            getSubscription(),
            getInvoices(),
        ]).then(([subRes, invRes]) => {
            const sub = subRes.data && subRes.data.subscription
            setSubscription(sub)
            if (sub) {
                setAnnual(sub.billing_period === 'annual')
                setSelectedPlan(sub.plan)
            }
            setInvoices((invRes.data && invRes.data.invoices) || [])
        }).finally(() => setLoading(false))
    }, [])

    const handleChange = () => {
        if (!selectedPlan) return
        setChanging(true)
        setChangeError(null)
        setChangeSuccess(false)

        changeSubscription({ data: { plan: selectedPlan, billing_period: annual ? 'annual' : 'monthly' } })
            .then(() => {
                setChangeSuccess(true)
                setSubscription(prev => prev ? { ...prev, plan: selectedPlan, billing_period: annual ? 'annual' : 'monthly' } : prev)
            })
            .catch(err => setChangeError((err.response && err.response.data && err.response.data.error) || 'Something went wrong.'))
            .finally(() => setChanging(false))
    }

    if (loading) return <Screen><Spin size="large" style={{ display: 'block', marginTop: 80 }} /></Screen>

    return (
        <Screen>
            <div style={{ maxWidth: 860, margin: '40px auto', padding: '0 24px' }}>
                <h1 style={{ marginBottom: 4 }}>Billing</h1>
                <p style={{ color: '#888', marginBottom: 32 }}>Manage your subscription plan.</p>

                {/* Current Plan Summary */}
                {subscription && (
                    <Card style={{ marginBottom: 32 }}>
                        <Row gutter={24} align="middle">
                            <Col flex="auto">
                                <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>Current Plan</div>
                                <div style={{ fontSize: 20, fontWeight: 600 }}>
                                    {(planDetails[subscription.plan] && planDetails[subscription.plan].label) || subscription.plan}
                                    {' — '}
                                    {subscription.billing_period === 'annual' ? 'Annual' : 'Monthly'}
                                </div>
                                {subscription.discount_code && (
                                    <div style={{ marginTop: 4 }}>
                                        <Tag color="green">
                                            {subscription.discount_code} — {discountLabels[subscription.discount_code] || subscription.discount_code}
                                        </Tag>
                                    </div>
                                )}
                            </Col>
                            <Col>
                                <Tag color={statusColor[subscription.status] || 'default'} style={{ fontSize: 14, padding: '4px 12px' }}>
                                    {subscription.status && subscription.status.replace('_', ' ').toUpperCase()}
                                </Tag>
                            </Col>
                        </Row>

                        {subscription.trial_end && (
                            <div style={{ marginTop: 12, color: '#1890ff' }}>
                                Trial ends: {moment.unix(subscription.trial_end).format('D MMM YYYY')}
                            </div>
                        )}
                        {subscription.current_period_end && (
                            <div style={{ marginTop: 4, color: '#888', fontSize: 13 }}>
                                Next billing date: {moment.unix(subscription.current_period_end).format('D MMM YYYY')}
                            </div>
                        )}
                    </Card>
                )}

                {false && (
                <Card title="Change Plan">
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                        <span style={{ marginRight: 10 }}>Monthly</span>
                        <Switch
                            checked={annual}
                            onChange={setAnnual}
                            checkedChildren="Annual"
                            unCheckedChildren="Monthly"
                        />
                        <span style={{ marginLeft: 10, color: annual ? '#52c41a' : '#888' }}>
                            Annual {annual ? '(save ~17%)' : ''}
                        </span>
                    </div>

                    <Row gutter={16}>
                        {planOrder.map(plan => {
                            const details  = planDetails[plan]
                            const isCurrent = subscription && subscription.plan === plan && subscription.billing_period === (annual ? 'annual' : 'monthly')
                            const isSelected = selectedPlan === plan

                            return (
                                <Col xs={24} sm={12} md={6} key={plan}>
                                    <Card
                                        hoverable={!isCurrent}
                                        onClick={() => !isCurrent && setSelectedPlan(plan)}
                                        style={{
                                            border: isSelected ? '2px solid #1890ff' : isCurrent ? '2px solid #52c41a' : '1px solid #e8e8e8',
                                            cursor: isCurrent ? 'default' : 'pointer',
                                            textAlign: 'center',
                                            marginBottom: 8,
                                        }}
                                        bodyStyle={{ padding: 16 }}
                                    >
                                        <div style={{ fontWeight: 600, fontSize: 15 }}>{details.label}</div>
                                        <div style={{ color: '#1890ff', fontSize: 18, margin: '8px 0' }}>
                                            {annual ? details.annual : details.monthly}
                                        </div>
                                        {isCurrent && <Tag color="green">Current</Tag>}
                                        {isSelected && !isCurrent && <Tag color="blue">Selected</Tag>}
                                    </Card>
                                </Col>
                            )
                        })}
                    </Row>

                    {changeSuccess && (
                        <Alert type="success" message="Plan updated successfully." style={{ marginTop: 16 }} showIcon />
                    )}
                    {changeError && (
                        <Alert type="error" message={changeError} style={{ marginTop: 16 }} showIcon />
                    )}

                    <Button
                        type="primary"
                        loading={changing}
                        disabled={!selectedPlan || (subscription && selectedPlan === subscription.plan && (annual ? 'annual' : 'monthly') === subscription.billing_period)}
                        onClick={handleChange}
                        style={{ marginTop: 20 }}
                    >
                        Confirm Plan Change
                    </Button>
                </Card>
                )}

                {/* Payment History */}
                <Card title="Payment History" style={{ marginTop: 32 }}>
                    {invoices.length === 0 ? (
                        <p style={{ color: '#aaa', margin: 0 }}>No payments yet.</p>
                    ) : (
                        <Table
                            dataSource={invoices}
                            columns={invoiceColumns}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        />
                    )}
                </Card>

                <Divider />

                {/* Cancel */}
                <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13 }}>
                    To cancel your subscription, email{' '}
                    <a href="mailto:cancel@leadpm.cloud">cancel@leadpm.cloud</a>
                </div>
            </div>
        </Screen>
    )
}

export default Billing
